import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useMemo, useCallback, useState, useRef } from 'react';
import { Modal, Loader, Tabs, createUseStyles, Text, BadgeNotification } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { useWeb5Context } from "../../../../lib/Web5Context";
import { getChartDynamicConfig, getTableDynamicConfig, getKpiDynamicConfig, getMapDynamicConfig } from "../../../../lib/services/forceBI";
import { operators, dateFieldOperators } from "../../../../components/Filters/operators";
import { infoToast } from "../../../../utils/toast";
import Charts from "../Charts";
import CrudFilters from "./components/CrudFilters";
import CrudFields from "./components/CrudFields";
import StepsInfo from "./components/StepsInfo";
import { getCrudSchema } from "./config";
import { NOT_CHART_TYPES } from "../../constants";
import Empty from "./components/Empty";
import useForceBIFilters from "../../hooks/useForceBIFilters";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'Crud');
const TABS = {
  chart: 'chart',
  filters: 'filters'
};
var _ref7 = /*#__PURE__*/React.createElement(Loader, {
  size: "big"
});
const Crud = /*#__PURE__*/memo(_ref => {
  let {
    isOpen,
    onClose,
    onAfterClose,
    onConfirm,
    onChange,
    data,
    filtersComponent,
    filtersAddButtonSchema,
    forceBIUtils,
    constants,
    crossFilterIdSeparator
  } = _ref;
  const classes = useStyles();
  const {
    t,
    getWeb4EntityFromString,
    entities
  } = useWeb5Context();
  const [tab, setTab] = useState(TABS.chart);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [config, setConfig] = useState(null);
  const [isError, setIsError] = useState(false);
  const [wrongFiltersMessage, setWrongFiltersMessage] = useState('');
  const [schema, setSchema] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const getWrongFiltersMessage = useCallback(error => {
    var _error$response;
    const {
      getFiltersErrorMessage
    } = forceBIUtils;
    const errorObj = ((_error$response = error.response) === null || _error$response === void 0 ? void 0 : _error$response.data) || null;
    if (errorObj !== null && errorObj !== void 0 && errorObj.error && errorObj !== null && errorObj !== void 0 && errorObj.fields && Object.keys(errorObj === null || errorObj === void 0 ? void 0 : errorObj.fields).length) {
      const message = getFiltersErrorMessage({
        message: errorObj.error,
        entityFields: errorObj.fields
      });
      setWrongFiltersMessage(message);
    }
  }, [forceBIUtils]);
  const getIsMap = useCallback(data => {
    if ((data === null || data === void 0 ? void 0 : data.chartType) === NOT_CHART_TYPES.map) return true;
    return false;
  }, []);
  const getIsKpi = useCallback(data => {
    if ((data === null || data === void 0 ? void 0 : data.chartType) === NOT_CHART_TYPES.kpi) return true;
    return false;
  }, []);
  const getIsTable = useCallback(data => {
    if ((data === null || data === void 0 ? void 0 : data.chartType) === NOT_CHART_TYPES.table) return true;
    return false;
  }, []);
  const completeLoad = useCallback(() => {
    setIsLoading(false);
    setIsLoaded(true);
  }, []);
  const updateVisuals = useCallback(_ref2 => {
    let {
      data
    } = _ref2;
    setIsError(false);
    setWrongFiltersMessage('');
    setIsLoading(true);
    setIsLoaded(false);
    const {
      processDataToSendFromCrud
    } = forceBIUtils;
    const newData = processDataToSendFromCrud({
      data
    });
    let promise = null;
    if (getIsMap(newData)) promise = getMapDynamicConfig(newData);else if (getIsKpi(newData)) promise = getKpiDynamicConfig(newData);else if (getIsTable(newData)) promise = getTableDynamicConfig(newData);else promise = getChartDynamicConfig(newData);
    if (!promise) return;
    promise.then(response => {
      if (!(response !== null && response !== void 0 && response.data)) {
        setIsError(false);
        setConfig(null);
        return;
      }
      setConfig(response.data);
    }).catch(error => {
      getWrongFiltersMessage(error);
      setConfig(null);
      setIsError(true);
    }).finally(() => completeLoad());
  }, [getIsTable, getIsKpi, getIsMap, forceBIUtils, completeLoad, getWrongFiltersMessage]);
  const getCanRenderChart = useCallback(data => {
    var _data$xAxis, _data$xAxisAggregatio, _data$entity, _data$yAxis, _data$xAxis2, _data$yAxisAggregatio;
    const hasXAxisDateField = (data === null || data === void 0 ? void 0 : (_data$xAxis = data.xAxis) === null || _data$xAxis === void 0 ? void 0 : _data$xAxis.isDateField) || false;
    const hasXAxisAggregationDate = data === null || data === void 0 ? void 0 : (_data$xAxisAggregatio = data.xAxisAggregationDate) === null || _data$xAxisAggregatio === void 0 ? void 0 : _data$xAxisAggregatio.value;
    const hasGeneralData = (data === null || data === void 0 ? void 0 : data.chartType) && (data === null || data === void 0 ? void 0 : (_data$entity = data.entity) === null || _data$entity === void 0 ? void 0 : _data$entity.value) && (data === null || data === void 0 ? void 0 : (_data$yAxis = data.yAxis) === null || _data$yAxis === void 0 ? void 0 : _data$yAxis.value) && (data === null || data === void 0 ? void 0 : (_data$xAxis2 = data.xAxis) === null || _data$xAxis2 === void 0 ? void 0 : _data$xAxis2.value) && (data === null || data === void 0 ? void 0 : (_data$yAxisAggregatio = data.yAxisAggregation) === null || _data$yAxisAggregatio === void 0 ? void 0 : _data$yAxisAggregatio.value);
    if (hasXAxisDateField) {
      if (hasGeneralData && hasXAxisAggregationDate) return true;
      return false;
    } else return hasGeneralData;
  }, []);
  const afterOnChangeFilter = useCallback(_ref3 => {
    let {
      filters,
      crossFilters,
      operators,
      crossOperators
    } = _ref3;
    if (!hasChanges) setHasChanges(true);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    const newData = _objectSpread(_objectSpread({}, data || {}), {}, {
      filters,
      crossFilters,
      operators,
      crossOperators
    });
    if (getCanRenderChart(newData)) {
      debounceTimeoutRef.current = setTimeout(() => {
        updateVisuals({
          data: newData
        });
      }, 300);
    }
  }, [data, getCanRenderChart, updateVisuals, hasChanges]);
  const forceBIFiltersProps = useForceBIFilters({
    afterOnChange: afterOnChangeFilter,
    constants
  });
  const numberOfFilters = useMemo(() => {
    const {
      getUniqueFilters
    } = forceBIUtils;
    const {
      filters,
      crossFilters,
      operators,
      crossOperators
    } = forceBIFiltersProps;
    const {
      uiFilterNames
    } = getUniqueFilters({
      filters,
      crossFilters,
      operators,
      crossOperators
    });
    return uiFilterNames.length || 0;
  }, [forceBIUtils, forceBIFiltersProps]);
  var _ref4 = /*#__PURE__*/React.createElement(BadgeNotification, {
    size: "small"
  }, numberOfFilters);
  const tabs = useMemo(() => {
    return [{
      key: TABS.chart,
      title: t('label_forcebi_crud_tab_chart')
    }, {
      key: TABS.filters,
      title: /*#__PURE__*/React.createElement("div", {
        className: classes.filtersTab
      }, /*#__PURE__*/React.createElement(Text, null, t('label_forcebi_crud_tab_filters')), numberOfFilters > 0 && _ref4)
    }];
  }, [numberOfFilters, t, classes]);

  // This filter exists to set a default filter depending on the entity and limitate the queries
  const createDynamicDefaultFilter = useCallback(value => {
    const {
      hasActiveFilters,
      customChangeOperator,
      customSaveCache
    } = forceBIFiltersProps;
    if (!hasActiveFilters) {
      const {
        CONTACTS,
        COMPANIES,
        OPPORTUNITIES,
        SALESORDERS,
        ACTIVITIES
      } = entities;
      const entity = getWeb4EntityFromString(value.value);
      let crossEntity = null;
      const reverseDateFieldOperators = Object.entries(dateFieldOperators.operators).reduce((obj, _ref5) => {
        let [key, value] = _ref5;
        obj[value] = key;
        return obj;
      }, {});
      const {
        THIS_YEAR
      } = operators;
      const fieldIdByEntity = {
        [CONTACTS.entity]: 'fcreado',
        [COMPANIES.entity]: 'fcreado',
        [OPPORTUNITIES.entity]: 'fcreado',
        [SALESORDERS.entity]: 'fcreado',
        [ACTIVITIES.entity]: 'date'
      };
      let fieldId = fieldIdByEntity[entity.entity];
      if (!fieldId) return;
      const operatorObj = {
        [fieldId]: {
          label: t(reverseDateFieldOperators[THIS_YEAR]),
          value: THIS_YEAR
        }
      };
      const cache = {
        visibleFilters: [],
        openFilters: {}
      };
      if (entity.entity !== COMPANIES.entity) {
        crossEntity = entity.entity;
        const crossFilter = `${entity.entity}${crossFilterIdSeparator}${fieldId}`;
        if (!cache.visibleFilters.includes(crossFilter)) {
          cache.visibleFilters.push(crossFilter);
        }
        cache.openFilters[crossFilter] = true;
      } else {
        if (!cache.visibleFilters.includes(fieldId)) {
          cache.visibleFilters.push(fieldId);
        }
        cache.openFilters[fieldId] = true;
      }
      customSaveCache(cache); // Make filter visible
      customChangeOperator({
        operators: operatorObj,
        crossEntity
      });
      let entityName = t(entity.labels.singular);
      entityName = entityName.charAt(0).toUpperCase() + entityName.slice(1).toLowerCase();
      infoToast({
        t,
        title: t('label_new_filter_added'),
        text: t('label_new_creation_date_filter_added_description', [entityName])
      });
    }
  }, [getWeb4EntityFromString, entities, crossFilterIdSeparator, t, forceBIFiltersProps]);
  const handleOnChange = useCallback(_ref6 => {
    let {
      fieldId,
      value
    } = _ref6;
    let newData = _objectSpread({}, data || {});
    if (!value && newData !== null && newData !== void 0 && newData.fieldId) {
      newData === null || newData === void 0 ? true : delete newData.fieldId;
    } else {
      newData[fieldId] = value;
    }
    if (fieldId === 'entity') {
      createDynamicDefaultFilter(value);
      newData.yAxis = null;
      newData.xAxis = null;
      newData.xAxisEntity = null;
      newData.xAxisBreakdown = null;
      newData.xAxisBreakdownEntity = null;
      newData.xAxisBreakdownAggregationDate = null;
      if (newData !== null && newData !== void 0 && newData.xAxisAggregationDate) {
        newData === null || newData === void 0 ? true : delete newData.xAxisAggregationDate;
      }
    } else if (fieldId === 'xAxisEntity') {
      newData.xAxis = null;
    } else if (fieldId === 'xAxisBreakdownEntity') {
      newData.xAxisBreakdown = null;
    } else if (fieldId === 'yAxis') {
      var _newData$yAxisAggrega;
      // Those yAxis options containing 'id' after '.' should be considered as numeric
      // and only have one yAxisAggregation method which is 'count'.
      const isNumeric = value.value.split('.')[1] === 'id';
      if (isNumeric && (newData === null || newData === void 0 ? void 0 : (_newData$yAxisAggrega = newData.yAxisAggregation) === null || _newData$yAxisAggrega === void 0 ? void 0 : _newData$yAxisAggrega.value) !== 'count') {
        newData.yAxisAggregation = null;
      }
    } else if (fieldId === 'xAxis' && !(value !== null && value !== void 0 && value.isDateField)) {
      if (newData !== null && newData !== void 0 && newData.xAxisAggregationDate) delete newData.xAxisAggregationDate;
    } else if (fieldId === 'xAxisBreakdown' && !(value !== null && value !== void 0 && value.isDateField)) {
      if (newData !== null && newData !== void 0 && newData.xAxisBreakdownAggregationDate) delete newData.xAxisBreakdownAggregationDate;
    }
    if (getCanRenderChart(newData) && fieldId !== 'title') {
      const {
        filters,
        crossFilters,
        operators,
        crossOperators
      } = forceBIFiltersProps;
      updateVisuals({
        data: _objectSpread(_objectSpread({}, newData), {}, {
          filters,
          crossFilters,
          operators,
          crossOperators
        })
      });
    }
    if (data && Object.keys(data).length > 0 && !hasChanges) setHasChanges(true);
    onChange && onChange(newData);
  }, [data, onChange, getCanRenderChart, updateVisuals, forceBIFiltersProps, hasChanges, createDynamicDefaultFilter]);
  const handleOnClose = useCallback(() => {
    setHasChanges(false);
    setIsDiscardOpen(false);
    onClose && onClose();
  }, [onClose]);
  const handleOnAfterClose = useCallback(() => {
    const {
      reset
    } = forceBIFiltersProps;
    reset();
    setConfig(null);
    onAfterClose && onAfterClose();
  }, [onAfterClose, forceBIFiltersProps]);
  const onAfterOpen = useCallback(() => {
    setTab(TABS.chart);
    setIsLoaded(false);
    setIsError(false);
    setErrors([]);
    const crudSchema = getCrudSchema({
      t
    });
    setSchema(crudSchema);
    const {
      init,
      reset
    } = forceBIFiltersProps;
    const {
      processFiltersToInitCrud
    } = forceBIUtils;
    if (data !== null && data !== void 0 && data.id) {
      setIsLoading(true);
      const initProps = processFiltersToInitCrud({
        filters: data.filters,
        crossFilters: data.crossFilters,
        operators: data.operators,
        crossOperators: data.crossOperators
      });
      init(_objectSpread({}, initProps));
      updateVisuals({
        data
      });
    } else {
      reset();
    }
  }, [data, forceBIFiltersProps, forceBIUtils, t, updateVisuals]);
  const getMandatoryFields = useCallback(() => {
    let mandatory = [];
    schema.forEach(current => {
      var _current$fields;
      if (current !== null && current !== void 0 && (_current$fields = current.fields) !== null && _current$fields !== void 0 && _current$fields.length) {
        current.fields.forEach(field => {
          var _field$subFields;
          if ((_field$subFields = field.subFields) !== null && _field$subFields !== void 0 && _field$subFields.length) {
            field.subFields.forEach(subField => {
              if (subField.isRequired) {
                mandatory.push(subField.id);
              }
            });
          } else if (field.getIsRequired && field.getIsRequired({
            fieldId: field.id,
            data
          })) {
            mandatory.push(field.id);
          } else if (field.isRequired) mandatory.push(field.id);
        });
      }
    });
    return mandatory;
  }, [schema, data]);
  const getErrors = useCallback(() => {
    const newErrors = getMandatoryFields().reduce((arr, current) => {
      if (!(data !== null && data !== void 0 && data[current])) arr.push(current);
      return arr;
    }, []);
    setErrors(newErrors);
    return newErrors;
  }, [data, getMandatoryFields]);
  const onOpenDiscardModal = useCallback(() => {
    if (hasChanges) return setIsDiscardOpen(true);
    handleOnClose();
  }, [hasChanges, handleOnClose]);
  const onCloseDiscardModal = useCallback(() => {
    setIsDiscardOpen(false);
  }, []);
  const handleOnConfirm = useCallback(() => {
    const newErrors = getErrors();
    if (newErrors !== null && newErrors !== void 0 && newErrors.length) {
      setTab(TABS.chart);
      return;
    }
    setIsConfirmLoading(true);
    const {
      processDataToSendFromCrud
    } = forceBIUtils;
    const {
      filters,
      crossFilters,
      operators,
      crossOperators
    } = forceBIFiltersProps;
    const newData = _objectSpread(_objectSpread({}, data), {}, {
      filters,
      crossFilters,
      operators,
      crossOperators
    });
    const finalData = processDataToSendFromCrud({
      data: newData
    });
    onConfirm && onConfirm({
      data: finalData
    }).then(() => {
      setTab(TABS.chart);
      setErrors([]);
      setIsConfirmLoading(false);
    }).catch(() => {
      setIsConfirmLoading(false);
    });
  }, [onConfirm, getErrors, data, forceBIUtils, forceBIFiltersProps]);
  const shouldDisplayInfo = useMemo(() => {
    var _data$xAxis3;
    const fields = ['chartType', 'entity', 'yAxis', 'xAxis', 'yAxisAggregation'];
    if (data !== null && data !== void 0 && (_data$xAxis3 = data.xAxis) !== null && _data$xAxis3 !== void 0 && _data$xAxis3.isDateField) fields.push('xAxisAggregationDate');
    const missingFields = fields.filter(current => !(data !== null && data !== void 0 && data[current]));
    return (missingFields === null || missingFields === void 0 ? void 0 : missingFields.length) > 0;
  }, [data]);
  const updateAfterError = useCallback(() => {
    const {
      filters,
      crossFilters,
      operators,
      crossOperators
    } = forceBIFiltersProps;
    const newData = _objectSpread(_objectSpread({}, data || {}), {}, {
      filters,
      crossFilters,
      operators,
      crossOperators
    });
    updateVisuals({
      data: newData
    });
  }, [data, forceBIFiltersProps, updateVisuals]);
  const onReturnToMainTab = useCallback(() => {
    if (tab === TABS.chart) return Promise.resolve();
    return new Promise(resolve => {
      setTab(TABS.chart);
      setTimeout(() => {
        resolve();
      }, 150);
    });
  }, [tab]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal, {
    className: classes.root,
    title: data !== null && data !== void 0 && data.id ? t('label_edit_widget') : t('label_forcebi_crud_title_placeholder'),
    size: "full",
    isOpen: isOpen,
    onRequestClose: onOpenDiscardModal,
    onAfterClose: handleOnAfterClose,
    onCancel: onOpenDiscardModal,
    onConfirm: handleOnConfirm,
    isConfirmLoading: isConfirmLoading,
    isConfirmDisabled: !!wrongFiltersMessage || isError,
    cancelText: t('action_forcebi_crud_cancel'),
    confirmText: t('action_forcebi_crud_save'),
    onAfterOpen: onAfterOpen,
    overrides: {
      content: {
        style: {
          height: '100%'
        }
      }
    },
    shouldCloseOnOverlayClick: false
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.container
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.left
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.leftContent
  }, isLoading && /*#__PURE__*/React.createElement("div", {
    className: classes.loader
  }, _ref7), !isLoading && getCanRenderChart(data) && !shouldDisplayInfo && !isError && /*#__PURE__*/React.createElement(Charts, {
    chartType: data.chartType,
    config: config
  }), isLoaded && !isLoading && isError && /*#__PURE__*/React.createElement(React.Fragment, null, wrongFiltersMessage && /*#__PURE__*/React.createElement(Empty, {
    type: "error",
    title: wrongFiltersMessage
  }), !wrongFiltersMessage && /*#__PURE__*/React.createElement(Empty, {
    type: "error",
    onRefresh: updateAfterError
  })), !isLoading && !getCanRenderChart(data) && shouldDisplayInfo && !isError && /*#__PURE__*/React.createElement(StepsInfo, {
    data: data,
    onReturnToMainTab: onReturnToMainTab
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.right
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.tabsContainer
  }, /*#__PURE__*/React.createElement(Tabs, {
    className: classes.tabs,
    onChange: setTab,
    tabs: tabs,
    activeKey: tab
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.formContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames(classes.crudFields, {
      [classes.hidden]: tab !== TABS.chart
    })
  }, /*#__PURE__*/React.createElement(CrudFields, {
    schema: schema,
    data: data,
    onChange: handleOnChange,
    errors: errors
  })), /*#__PURE__*/React.createElement("div", {
    className: classnames(classes.crudFilters, {
      [classes.hidden]: tab !== TABS.filters
    })
  }, /*#__PURE__*/React.createElement(CrudFilters, {
    entity: forceBIUtils.entity,
    filtersComponent: filtersComponent,
    filtersAddButtonSchema: filtersAddButtonSchema,
    forceBIFiltersProps: forceBIFiltersProps
  })))))), /*#__PURE__*/React.createElement(Modal, {
    isOpen: isDiscardOpen,
    size: "small",
    title: t('action_discard_changes'),
    confirmText: t('action_confirm_lose_changes'),
    cancelText: t('action_cancel'),
    onConfirm: handleOnClose,
    onCancel: onCloseDiscardModal,
    onRequestClose: onCloseDiscardModal,
    useCornerClose: false,
    isConfirmDisabled: isConfirmLoading,
    isConfirmLoading: isConfirmLoading
  }, /*#__PURE__*/React.createElement(Text, null, t('label_discard_unsaved_changes'))));
});
export default Crud;